<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { buildFilterList, buildRatingsList } from './CsatFilterHelpers';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import PeriodPicker from 'dashboard/components-next/filter-bar/PeriodPicker.vue';
import { periodFromRange } from 'dashboard/components-next/filter-bar/period';
import {
  parseReportURLParams,
  parseFilterURLParams,
  generateCompleteURLParams,
} from '../../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const props = defineProps({
  showTeamFilter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

// Initialize from URL params immediately
const urlParams = parseReportURLParams(route.query);
const urlFilters = parseFilterURLParams(route.query);

// UI-FILTROS-01 (UF-45): o período do PeriodPicker, `{ range, from, to }`, com
// `from` e `to` em segundos, do início ao fim do dia. Com nome, o da URL sempre
// traz as datas (`resolvePeriod`); sem, os últimos 7 dias, como antes.
const period = ref(
  urlParams.range
    ? {
        range: urlParams.range,
        from: getUnixStartOfDay(new Date(urlParams.from * 1000)),
        to: getUnixEndOfDay(new Date(urlParams.to * 1000)),
      }
    : periodFromRange(DATE_RANGE_TYPES.LAST_7_DAYS)
);

const appliedFilters = ref({
  user_ids: urlFilters.agent_id,
  inbox_id: urlFilters.inbox_id,
  team_id: urlFilters.team_id,
  rating: urlFilters.rating,
});

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);

const ratings = computed(() => buildRatingsList(t));

const from = computed(() => period.value.from);
const to = computed(() => period.value.to);

// Os chips v3 viram as pílulas da barra padrão (FilterBar), todas `single` e
// `scalar`, porque a API recebe um id só (UI-FILTROS-01 PLAN §8). O time aparece
// com a flag, ou quando já vem escolhido (de um link), como o chip ativo de antes.
const fields = computed(() =>
  [
    {
      key: 'user_ids',
      label: t('CSAT_REPORTS.FILTERS.AGENTS.LABEL'),
      options: buildFilterList(agents.value, 'agents'),
    },
    {
      key: 'inbox_id',
      label: t('CSAT_REPORTS.FILTERS.INBOXES.LABEL'),
      options: buildFilterList(inboxes.value, 'inboxes'),
    },
    ...(props.showTeamFilter || appliedFilters.value.team_id
      ? [
          {
            key: 'team_id',
            label: t('CSAT_REPORTS.FILTERS.TEAMS.LABEL'),
            options: buildFilterList(teams.value, 'teams'),
          },
        ]
      : []),
    {
      key: 'rating',
      label: t('CSAT_REPORTS.FILTERS.RATINGS.LABEL'),
      options: ratings.value,
    },
  ].map(field => ({ ...field, single: true, scalar: true }))
);

const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: from.value,
    to: to.value,
    range: period.value.range,
    filters: {
      agent_id: appliedFilters.value.user_ids,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      rating: appliedFilters.value.rating,
    },
  });
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    selectedAgents: appliedFilters.value.user_ids
      ? [{ id: appliedFilters.value.user_ids }]
      : [],
    selectedInbox: appliedFilters.value.inbox_id
      ? { id: appliedFilters.value.inbox_id }
      : null,
    selectedTeam: appliedFilters.value.team_id
      ? { id: appliedFilters.value.team_id }
      : null,
    selectedRating: appliedFilters.value.rating
      ? { value: appliedFilters.value.rating }
      : null,
  });
};

const onFiltersChange = filters => {
  appliedFilters.value = filters;
  emitChange();
};

const clearAllFilters = () => {
  appliedFilters.value = {
    user_ids: null,
    inbox_id: null,
    team_id: null,
    rating: null,
  };
  emitChange();
};

const onPeriodChange = value => {
  period.value = value;
  emitChange();
};

onMounted(() => {
  emitChange();
});
</script>

<template>
  <FilterBar
    :model-value="appliedFilters"
    :fields="fields"
    :search="false"
    @update:model-value="onFiltersChange"
    @reset="clearAllFilters"
  >
    <template #period>
      <PeriodPicker
        :model-value="period"
        @update:model-value="onPeriodChange"
      />
    </template>
  </FilterBar>
</template>
